import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Session, User, AuthError } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { queryClient } from '@/lib/queryClient';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isReady: boolean;
  signUp: (email: string, password: string, metadata?: Record<string, any>) => Promise<{ user: User | null; session: Session | null; error: AuthError | null }>;
  signIn: (email: string, password: string) => Promise<{ user: User | null; session: Session | null; error: AuthError | null }>;
  signInWithMagicLink: (email: string, metadata?: Record<string, any>, redirectTo?: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<{ error: AuthError | null }>;
  resetPassword: (email: string) => Promise<{ data: {} | null; error: AuthError | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: AuthError | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    // Self-healing initial session fetch.
    //
    // Background: when localStorage holds a stale, mismatched, or
    // unrefreshable Supabase token (common after Supabase project / key /
    // URL drift), `supabase.auth.getSession()` can hang or reject during
    // the implicit token-refresh attempt. Without a catch + timeout, the
    // promise never resolves, `isReady` stays false, and `ProtectedRoute`
    // shows a spinner forever — even though the bug is just a bad token
    // sitting in localStorage.
    //
    // We:
    //   1. Race the call against a hard timeout so the app can never get
    //      stranded on the spinner.
    //   2. On rejection or timeout, clear the local Supabase storage entry
    //      via a local-scope sign-out so the next reload starts clean.
    //   3. Always flip `isReady` to true at the end, even on failure —
    //      worst case the user is sent to /login (recoverable) instead of
    //      stuck on a spinner (not recoverable without DevTools).
    const SESSION_BOOT_TIMEOUT_MS = 6000;

    const clearBadToken = async () => {
      try {
        await supabase.auth.signOut({ scope: 'local' });
      } catch {
        // Best-effort; if it fails the user can still recover via /login.
      }
    };

    // True when the current URL carries an in-flight auth callback (the
    // implicit magic-link / OAuth flow puts the tokens in the hash). When this
    // is present we must NOT clear tokens on a slow boot: detectSessionInUrl +
    // onAuthStateChange will still finish establishing the session, and wiping
    // it here is exactly what bounced mobile magic-link users to a spinner.
    const hasAuthCallbackInUrl = () => {
      const blob = `${window.location.hash || ''}${window.location.search || ''}`;
      return /(?:[#&?]|^)(access_token|refresh_token)=/.test(blob);
    };

    const bootSession = async () => {
      let timedOut = false;
      let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
      const timeoutPromise = new Promise<{ data: { session: Session | null } }>((_, reject) => {
        timeoutHandle = setTimeout(() => {
          timedOut = true;
          reject(new Error('supabase.auth.getSession() timed out'));
        }, SESSION_BOOT_TIMEOUT_MS);
      });

      try {
        const { data } = await Promise.race([
          supabase.auth.getSession(),
          timeoutPromise,
        ]);
        if (cancelled) return;
        setSession(data.session);
        setUser(data.session?.user || null);
      } catch (err) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.warn(
          '[auth] Initial session fetch failed' + (timedOut ? ' (timeout)' : ''),
          err,
        );
        if (!hasAuthCallbackInUrl()) {
          // Fire-and-forget: awaiting signOut here can itself hang on mobile /
          // incognito browsers (restricted storage), which would stop the
          // finally block from ever flipping isReady → permanent spinner.
          void clearBadToken();
          setSession(null);
          setUser(null);
        }
      } finally {
        if (timeoutHandle) clearTimeout(timeoutHandle);
        if (!cancelled) setIsReady(true);
      }
    };

    void bootSession();

    // Listen for auth changes. Also covers the case where a session goes
    // bad mid-app-life (e.g. refresh token rejected later) — we clear our
    // local state so ProtectedRoute can route to /login instead of
    // rendering with a half-valid session.
    const { data: subscription } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;
      if (event === 'SIGNED_OUT' || (event === 'TOKEN_REFRESHED' && !session)) {
        setSession(null);
        setUser(null);
        return;
      }
      setSession(session);
      setUser(session?.user || null);
    });

    return () => {
      cancelled = true;
      subscription.subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string, metadata?: Record<string, any>) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata,
      },
    });

    return { user: data.user, session: data.session, error };
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    return { user: data.user, session: data.session, error };
  };

  const signInWithMagicLink = async (email: string, metadata?: Record<string, any>, redirectTo?: string) => {
    let emailRedirectUrl = `${window.location.origin}/login`;
    if (redirectTo) {
      emailRedirectUrl = `${window.location.origin}/login?redirect=${encodeURIComponent(redirectTo)}`;
    }
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: true,
        emailRedirectTo: emailRedirectUrl,
        data: metadata,
      },
    });

    return { error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    
    if (!error) {
      // Clear React Query cache after successful logout
      queryClient.clear();
      // Navigate to login page
      window.location.assign('/login');
    }
    
    return { error };
  };

  const resetPassword = async (email: string) => {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    return { data, error };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    return { error };
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        isReady,
        signUp,
        signIn,
        signInWithMagicLink,
        signOut,
        resetPassword,
        updatePassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}